<script module lang="ts">
	// Set up this till (T27, UC-59, #774): after a till pairs, a manager walks
	// through its setup steps over Sell, one page per step the screen knows,
	// each one skippable. Till.svelte decides when it opens and what a pass
	// leads to; the step list (src/till/setupSteps.ts) gives the bottom row.
	// On a Windows till the sharing step (T28, #775) comes first; the
	// cash-point step (T32, #792) follows printers, and its Next or Confirm
	// first sends the picked cash point.

	/**
	 * Which action says it is saving: the pressed one, once its wait passed
	 * 300 ms; Next or Confirm also while the cash point it sends is slow.
	 */
	export function setupSaving(saving: 'skip' | 'next' | null, slow: boolean, sending = false) {
		return { skip: saving === 'skip' && slow, next: (saving === 'next' && slow) || sending };
	}
</script>

<script lang="ts">
	import Band from '../components/Band.svelte';
	import Sheet from '../components/Sheet.svelte';
	import CashPointStep from './CashPointStep.svelte';
	import PrintersStep from './PrintersStep.svelte';
	import SharingStep from './SharingStep.svelte';
	import { message, type Language } from '../i18n/messages';
	import type { TillCashPoint } from '../till/cashPoint.svelte';
	import type { TillMesh } from '../till/mesh.svelte';
	import type { Printer } from '../till/printers.svelte';
	import type { TillSetup } from '../till/setup.svelte';
	import {
		isLastOpenStep,
		knownSteps,
		setupFigure,
		stepActions,
		type StepContext,
	} from '../till/setupSteps';
	interface Props {
		open: boolean;
		language: Language;
		setup: TillSetup;
		index: number;
		context: StepContext;
		printers: Printer[];
		mesh: TillMesh;
		cashPoint: TillCashPoint;
		/** This till's own id, the cash-point step's This till. */
		selfId: string;
		onBack: () => void;
		onPass: (passed: 'done' | 'skipped') => void;
		onClose: () => void;
		onPrinters: () => void;
	}
	let {
		open,
		language,
		setup,
		index,
		context,
		printers,
		mesh,
		cashPoint,
		selfId,
		onBack,
		onPass,
		onClose,
		onPrinters,
	}: Props = $props();
	const steps = $derived(knownSteps(setup.setup));
	const step = $derived(steps[index]);
	const actions = $derived(
		stepActions(isLastOpenStep(setup.setup, index), step?.isSet(context) ?? false),
	);
	const saving = $derived(setupSaving(setup.saving, setup.savingSlow, cashPoint.savingSlow));
	// A pass, or the cash point its Next sends, is on its way.
	const busy = $derived(setup.saving !== null || cashPoint.saving);
</script>

<Sheet
	{open}
	level={1}
	title={message(language, 'setupTitle')}
	meta={setupFigure(index, steps.length)}
	backLabel={index > 0 ? message(language, 'printersBack') : undefined}
	onBack={index > 0 ? onBack : undefined}
	backTestId="setup-back"
	{onClose}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="setup-close"
	primaryLabel={message(language, actions.primary)}
	primaryIcon={actions.icon}
	primaryState={saving.next ? 'pending' : 'idle'}
	pendingLabel={message(language, 'saving')}
	primaryDisabled={actions.disabled || busy}
	primaryTestId="setup-next"
	onPrimary={() => onPass('done')}
	secondaryLabel={actions.skip
		? message(language, saving.skip ? 'saving' : 'setupSkip')
		: undefined}
	secondaryDisabled={busy}
	secondaryTestId="setup-skip"
	onSecondary={() => onPass('skipped')}
	locked={busy}
	testId="setup-sheet"
>
	<div class="body">
		{#if setup.failure}<Band tone="flag" alert testId="setup-failure"
				>{message(language, 'printerFailed')}</Band
			>{/if}
		{#if step?.id === 'sharing'}<SharingStep
				{language}
				{mesh}
				disabled={busy}
			/>{:else if step?.id === 'printers'}<PrintersStep
				{language}
				{printers}
				disabled={busy}
				onOpen={onPrinters}
			/>{:else if step?.id === 'cash_point'}<CashPointStep
				{language}
				{cashPoint}
				{mesh}
				{selfId}
				disabled={busy}
			/>{/if}
	</div>
</Sheet>

<style>
	.body {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}
</style>
